"use client";

import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMenuStore } from "@/stores/menuStore";
import { MenuItem, AvailableCategory, AvailablePage } from "@/types/menu";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

import {
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  GripVertical,
  Save,
  RefreshCw,
  Search,
  Folder,
  FileText,
} from "lucide-react";

import { showToast } from "@/lib/toast";

interface MenuItemComponentProps {
  item: MenuItem;
  index: string | number;
  level?: number;
  isEditing?: boolean;
  editingLabel?: string;
  isExpanded?: boolean;
  editingIndex?: string | number;
  expandedItems?: Set<string>;
  onStartEdit: (index: string | number, currentLabel: string) => void;
  onSaveEdit: (index: string | number) => void;
  onCancelEdit: () => void;
  onLabelChange: (value: string) => void;
  onDelete: (index: string | number) => void;
  onMoveUp: (index: string | number) => void;
  onMoveDown: (index: string | number) => void;
  onToggleExpand: (index: string | number) => void;
}

function MenuItemComponent({
  item,
  index,
  level = 0,
  isEditing = false,
  editingLabel = "",
  isExpanded = true,
  editingIndex = -1,
  expandedItems = new Set(),
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onLabelChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  onToggleExpand,
}: MenuItemComponentProps) {
  const indent = level * 40; // 20px per level

  return (
    <div className="space-y-2">
      <div
        className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        style={{ marginLeft: `${indent}px` }}
      >
        <div className="flex items-center gap-2 flex-1">
          {/* Expand/Collapse button for items with children */}
          {item.children && item.children.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleExpand(index)}
              className="h-6 w-6 p-0 hover:bg-gray-100"
              title={isExpanded ? "Thu gọn" : "Mở rộng"}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </Button>
          ) : (
            <div className="w-6" /> // Spacer for alignment
          )}

          {item.type === "category" ? (
            <Folder className="w-5 h-5 text-blue-500" />
          ) : (
            <FileText className="w-5 h-5 text-green-500" />
          )}

          <div className="flex-1">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editingLabel}
                  onChange={(e) => onLabelChange(e.target.value)}
                  className="flex-1"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSaveEdit(index)}
                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                  title="Lưu"
                >
                  <Save className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancelEdit}
                  className="h-8 w-8 p-0 text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  title="Hủy"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <span className="font-medium text-gray-900 dark:text-white">
                  {item.label}
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <Badge
                    variant={item.type === "category" ? "default" : "secondary"}
                    className="text-xs"
                  >
                    {item.type === "category" ? "Danh mục" : "Trang"}
                  </Badge>
                  {level > 0 && (
                    <Badge variant="outline" className="text-xs">
                      Cấp {level + 1}
                    </Badge>
                  )}
                  {item.children && item.children.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {item.children.length} con
                    </Badge>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-1">
            {typeof index === "number" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMoveUp(index)}
                  className="h-8 w-8 p-0"
                  title="Di chuyển lên"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMoveDown(index)}
                  className="h-8 w-8 p-0"
                  title="Di chuyển xuống"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onStartEdit(index, item.label)}
              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
              title="Chỉnh sửa label"
            >
              <Edit className="w-4 h-4" />
            </Button>

            {typeof index === "number" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(index)}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                title="Xóa"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Render children recursively - only if expanded */}
      {item.children && item.children.length > 0 && isExpanded && (
        <div className="space-y-2">
          {item.children.map((child, childIndex) => (
            <MenuItemComponent
              key={`${child.id}-${childIndex}`}
              item={child}
              index={`${index}-${childIndex}`} // Create unique index for children
              level={level + 1}
              isEditing={editingIndex.toString() === `${index}-${childIndex}`}
              editingLabel={editingLabel}
              isExpanded={expandedItems.has(`${index}-${childIndex}`)}
              editingIndex={editingIndex}
              expandedItems={expandedItems}
              onStartEdit={onStartEdit}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              onLabelChange={onLabelChange}
              onDelete={onDelete}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MenuPage() {
  const {
    menu,
    isLoading,
    isUpdating,
    error,
    availableCategories,
    availablePages,
    fetchMenu,
    updateMenu,
    fetchAvailableCategories,
    fetchAvailablePages,
  } = useMenuStore();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<string | number>(-1);
  const [editingLabel, setEditingLabel] = useState<string>("");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<"category" | "page">(
    "category"
  );
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [customLabel, setCustomLabel] = useState("");

  // Load menu on component mount
  useEffect(() => {
    fetchMenu();
    fetchAvailableCategories();
    fetchAvailablePages();
  }, [fetchMenu, fetchAvailableCategories, fetchAvailablePages]);

  // Auto-expand all items only when menu loads for the first time
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (menu?.menuTree && isFirstLoad) {
      const allIndices = new Set<string>();

      const collectAllIndices = (
        items: MenuItem[],
        parentPath: string = ""
      ) => {
        items.forEach((item, index) => {
          const currentPath = parentPath
            ? `${parentPath}-${index}`
            : index.toString();
          allIndices.add(currentPath);

          if (item.children && item.children.length > 0) {
            collectAllIndices(item.children, currentPath);
          }
        });
      };

      collectAllIndices(menu.menuTree);
      setExpandedItems(allIndices);
      setIsFirstLoad(false);
    }
  }, [menu, isFirstLoad]);

  // Handle search for available items
  const handleSearch = useCallback(
    (search: string) => {
      setSearchTerm(search);
      if (selectedType === "category") {
        fetchAvailableCategories(search);
      } else {
        fetchAvailablePages(search);
      }
    },
    [selectedType, fetchAvailableCategories, fetchAvailablePages]
  );

  // Handle add new menu item
  const handleAddItem = () => {
    setIsAddModalOpen(true);
    setSelectedType("category");
    setSelectedItem("");
    setCustomLabel("");
  };

  // Handle start inline edit
  const handleStartEdit = (index: string | number, currentLabel: string) => {
    setEditingIndex(index);
    setEditingLabel(currentLabel);
  };

  // Handle save inline edit
  const handleSaveEdit = async (index: string | number) => {
    if (!menu || !editingLabel.trim()) {
      showToast.error("Vui lòng nhập label");
      return;
    }

    if (typeof index === "number") {
      // Edit root level item
      const newMenuTree = [...menu.menuTree];
      newMenuTree[index] = {
        ...newMenuTree[index],
        label: editingLabel.trim(),
      };

      await updateMenu({ menuTree: newMenuTree });
      showToast.success("Cập nhật label thành công");
      setEditingIndex(-1);
      setEditingLabel("");
    } else {
      // Edit child item - need to find and update in nested structure
      const updateNestedItem = (items: MenuItem[], path: string[]): boolean => {
        if (path.length === 1) {
          const itemIndex = parseInt(path[0]);
          if (itemIndex >= 0 && itemIndex < items.length) {
            items[itemIndex].label = editingLabel.trim();
            return true;
          }
        } else {
          const parentIndex = parseInt(path[0]);
          if (
            parentIndex >= 0 &&
            parentIndex < items.length &&
            items[parentIndex].children
          ) {
            return updateNestedItem(
              items[parentIndex].children!,
              path.slice(1)
            );
          }
        }
        return false;
      };

      const pathParts = index.toString().split("-");
      const newMenuTree = [...menu.menuTree];

      if (updateNestedItem(newMenuTree, pathParts)) {
        await updateMenu({ menuTree: newMenuTree });
        showToast.success("Cập nhật label thành công");
        setEditingIndex(-1);
        setEditingLabel("");
      } else {
        showToast.error("Không thể cập nhật item");
      }
    }
  };

  // Handle cancel inline edit
  const handleCancelEdit = () => {
    setEditingIndex(-1);
    setEditingLabel("");
  };

  // Handle label change
  const handleLabelChange = (value: string) => {
    setEditingLabel(value);
  };

  // Handle toggle expand/collapse
  const handleToggleExpand = (index: string | number) => {
    const indexStr = index.toString();
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(indexStr)) {
        newSet.delete(indexStr);
      } else {
        newSet.add(indexStr);
      }
      return newSet;
    });
  };

  // Handle delete menu item
  const handleDeleteItem = async (index: string | number) => {
    if (!menu) return;

    // Only allow deleting root level items (numeric index)
    if (typeof index === "number") {
      const newMenuTree = [...menu.menuTree];
      newMenuTree.splice(index, 1);

      await updateMenu({ menuTree: newMenuTree });
      showToast.success("Xóa menu item thành công");
    } else {
      showToast.error("Chỉ có thể xóa danh mục gốc");
    }
  };

  // Handle move up
  const handleMoveUp = async (index: string | number) => {
    if (!menu) return;

    // Only allow moving root level items (numeric index)
    if (typeof index === "number" && index > 0) {
      const newMenuTree = [...menu.menuTree];
      [newMenuTree[index - 1], newMenuTree[index]] = [
        newMenuTree[index],
        newMenuTree[index - 1],
      ];

      await updateMenu({ menuTree: newMenuTree });
      showToast.success("Di chuyển menu item lên thành công");
    } else if (typeof index === "string") {
      showToast.error("Chỉ có thể di chuyển danh mục gốc");
    }
  };

  // Handle move down
  const handleMoveDown = async (index: string | number) => {
    if (!menu) return;

    // Only allow moving root level items (numeric index)
    if (typeof index === "number" && index < menu.menuTree.length - 1) {
      const newMenuTree = [...menu.menuTree];
      [newMenuTree[index], newMenuTree[index + 1]] = [
        newMenuTree[index + 1],
        newMenuTree[index],
      ];

      await updateMenu({ menuTree: newMenuTree });
      showToast.success("Di chuyển menu item xuống thành công");
    } else if (typeof index === "string") {
      showToast.error("Chỉ có thể di chuyển danh mục gốc");
    }
  };

  // Convert category with children to menu items (single root with nested children)
  const convertCategoryToMenuItems = (
    category: AvailableCategory
  ): MenuItem => {
    const children: MenuItem[] = [];

    // Add children recursively
    if ((category as any).children && (category as any).children.length > 0) {
      (category as any).children.forEach((child: any) => {
        children.push(convertCategoryToMenuItems(child));
      });
    }

    return {
      type: "category",
      id: category._id,
      label: category.name,
      children: children,
    };
  };

  // Handle save new item
  const handleSaveNewItem = async () => {
    if (!selectedItem) {
      showToast.error("Vui lòng chọn item");
      return;
    }

    if (selectedType === "page" && !customLabel.trim()) {
      showToast.error("Vui lòng nhập label cho trang");
      return;
    }

    if (!menu) return;

    let newItem: MenuItem;

    if (selectedType === "category") {
      // Find the selected category
      const selectedCategory = availableCategories.find(
        (cat) => cat._id === selectedItem
      );
      if (selectedCategory) {
        // Convert category with children to menu item (single root with nested children)
        newItem = convertCategoryToMenuItems(selectedCategory);
        // Override the root label with custom label if provided
        if (customLabel.trim()) {
          newItem.label = customLabel.trim();
        }
      } else {
        // Fallback to single item
        newItem = {
          type: selectedType,
          id: selectedItem,
          label: customLabel.trim(),
          children: [],
        };
      }
    } else {
      // For pages, just add single item
      newItem = {
        type: selectedType,
        id: selectedItem,
        label: customLabel.trim(),
        children: [],
      };
    }

    const newMenuTree = [...menu.menuTree];
    newMenuTree.push(newItem);

    await updateMenu({ menuTree: newMenuTree });
    showToast.success("Thêm menu item thành công");
    setIsAddModalOpen(false);
    setSelectedItem("");
    setCustomLabel("");
  };

  // Get available items based on selected type (only root categories)
  const getAvailableItems = () => {
    if (selectedType === "category") {
      const categories = availableCategories || [];
      // Only return root categories (level 0)
      return categories.filter((cat) => cat.level === 0);
    } else {
      return availablePages || [];
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-500 dark:text-gray-400">Đang tải menu...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium mb-2">
              Lỗi khi tải menu
            </div>
            <div className="text-gray-500 dark:text-gray-400 mb-4">{error}</div>
            <Button onClick={() => fetchMenu()}>Thử lại</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
              Quản lý Menu
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <p className="text-gray-500 dark:text-gray-400">
                Quản lý cấu trúc menu của website
              </p>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                Cấu hình
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => fetchMenu()}
              variant="outline"
              disabled={isLoading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Làm mới
            </Button>
          </div>
        </div>

        {/* Menu Tree */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GripVertical className="w-5 h-5" />
              Cấu trúc Menu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Main Category */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Folder className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900">
                        Danh mục chính
                      </h3>
                      <p className="text-sm text-blue-700">
                        Quản lý các trang và danh mục con
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddItem()}
                      className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Thêm trang/danh mục
                    </Button>
                  </div>
                </div>
              </div>

              {/* Sub Items */}
              <div className="space-y-2 ml-4">
                {menu?.menuTree && menu.menuTree.length > 0 ? (
                  menu.menuTree.map((item, index) => (
                    <MenuItemComponent
                      key={`${item.id}-${index}`}
                      item={item}
                      index={index}
                      isEditing={editingIndex.toString() === index.toString()}
                      editingLabel={editingLabel}
                      isExpanded={expandedItems.has(index.toString())}
                      editingIndex={editingIndex}
                      expandedItems={expandedItems}
                      onStartEdit={handleStartEdit}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={handleCancelEdit}
                      onLabelChange={handleLabelChange}
                      onDelete={handleDeleteItem}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      onToggleExpand={handleToggleExpand}
                    />
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">
                      Chưa có trang hoặc danh mục con nào
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Nhấn "Thêm trang/danh mục" để bắt đầu
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Modal */}
        <Dialog
          open={isAddModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsAddModalOpen(false);
              setSelectedItem("");
              setCustomLabel("");
              setSearchTerm("");
            }
          }}
        >
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Thêm menu item</DialogTitle>
              <DialogDescription>
                Thêm trang hoặc danh mục mới vào menu
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="type">Loại</Label>
                <Select
                  value={selectedType}
                  onValueChange={(value: "category" | "page") => {
                    setSelectedType(value);
                    setSelectedItem("");
                    setSearchTerm("");
                    if (value === "category") {
                      fetchAvailableCategories();
                    } else {
                      fetchAvailablePages();
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="category">Category</SelectItem>
                    <SelectItem value="page">Page</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="search">Tìm kiếm</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder="Tìm kiếm..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="item">
                  Chọn {selectedType === "category" ? "Category" : "Page"}
                </Label>
                <Select value={selectedItem} onValueChange={setSelectedItem}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={`Chọn ${
                        selectedType === "category" ? "category" : "page"
                      }...`}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableItems().map((item) => {
                      if (selectedType === "category") {
                        const category = item as AvailableCategory;
                        return (
                          <SelectItem key={category._id} value={category._id}>
                            {category.name}
                            {(category as any).children &&
                              (category as any).children.length > 0 &&
                              ` (${
                                (category as any).children.length
                              } danh mục con)`}
                          </SelectItem>
                        );
                      } else {
                        const page = item as AvailablePage;
                        return (
                          <SelectItem key={page._id} value={page._id}>
                            {page.title}
                          </SelectItem>
                        );
                      }
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="label">
                  Label hiển thị{" "}
                  {selectedType === "category"
                    ? "(để trống sẽ dùng tên gốc)"
                    : ""}
                </Label>
                <Input
                  id="label"
                  placeholder={
                    selectedType === "category"
                      ? "Để trống sẽ dùng tên danh mục gốc..."
                      : "Nhập label hiển thị..."
                  }
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setSelectedItem("");
                  setCustomLabel("");
                  setSearchTerm("");
                }}
              >
                Hủy
              </Button>
              <Button
                onClick={handleSaveNewItem}
                disabled={
                  !selectedItem ||
                  (selectedType === "page" && !customLabel.trim()) ||
                  isUpdating
                }
              >
                <Save className="w-4 h-4 mr-2" />
                {isUpdating ? "Đang lưu..." : "Lưu"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
